export const IDV_VENDOR_SCRIPT_PATH = '/sfr_api/serve-file';

export const isIdvVendorScriptSrc = (src) => (
  typeof src === 'string' && src.indexOf(IDV_VENDOR_SCRIPT_PATH) !== -1
);

const isAlreadyRegistered = (registry, name) => (
  typeof registry.get === 'function' && Boolean(registry.get(name))
);

const isNotSupportedError = (error) => Boolean(error && error.name === 'NotSupportedError');

/**
 * Prevents NotSupportedError when a vendor bundle (IDV / lottie-player)
 * calls customElements.define for a name that is already registered.
 * NYLIBS-676.
 */
export function guardCustomElementsDefine(registry) {
  const target = registry
    || (typeof customElements !== 'undefined' ? customElements : null);

  if (!target || typeof target.define !== 'function') {
    return;
  }

  if (target.define.__nylCustomElementGuard) {
    return;
  }

  const originalDefine = target.define.bind(target);

  const guardedDefine = function guardedDefine(name, constructor, options) {
    if (isAlreadyRegistered(target, name)) {
      return undefined;
    }

    try {
      return originalDefine(name, constructor, options);
    } catch (error) {
      if (isNotSupportedError(error)) {
        return undefined;
      }
      throw error;
    }
  };

  guardedDefine.__nylCustomElementGuard = true;
  target.define = guardedDefine;
}

const createDuplicateSrcFilter = (loadedSrcs) => (node) => {
  if (!node || node.nodeName !== 'SCRIPT') {
    return false;
  }

  const { src } = node;
  if (!isIdvVendorScriptSrc(src)) {
    return false;
  }

  if (loadedSrcs[src]) {
    return true;
  }

  loadedSrcs[src] = true;
  return false;
};

/**
 * Skip inserting the same IDV vendor script URL twice in one page session.
 * GTM uses Node#insertBefore; keep this patch on Node.prototype.
 */
export function guardDuplicateIdvScripts(options = {}) {
  const nodePrototype = options.nodePrototype
    || (typeof Node !== 'undefined' ? Node.prototype : null);

  if (!nodePrototype || typeof nodePrototype.appendChild !== 'function') {
    return;
  }

  if (nodePrototype.appendChild.__nylIdvScriptGuard) {
    return;
  }

  const shouldBlock = createDuplicateSrcFilter(Object.create(null));
  const originalAppendChild = nodePrototype.appendChild;
  const originalInsertBefore = nodePrototype.insertBefore;

  nodePrototype.appendChild = function appendChild(child) {
    if (shouldBlock(child)) {
      return child;
    }
    return originalAppendChild.call(this, child);
  };
  nodePrototype.appendChild.__nylIdvScriptGuard = true;

  if (typeof originalInsertBefore === 'function') {
    nodePrototype.insertBefore = function insertBefore(newNode, referenceNode) {
      if (shouldBlock(newNode)) {
        return newNode;
      }
      return originalInsertBefore.call(this, newNode, referenceNode);
    };
  }
}

export function installIdvVendorGuards(options = {}) {
  guardCustomElementsDefine(options.registry);
  guardDuplicateIdvScripts({ nodePrototype: options.nodePrototype });
}
